import { createServer, type Server as HttpServer, type IncomingMessage, type ServerResponse } from "node:http";
import { createRequire } from "node:module";
import {
	type CompactionPreparationOptions,
	type CompactionSettings,
	compactLegacy,
	DEFAULT_COMPACTION_SETTINGS,
	type LegacyCompactResult,
	type ProxyAssistantMessageEvent,
	prepareLegacyCompaction,
	type SessionTreeEntry,
} from "@earendil-works/pi-agent-core";
import {
	type AssistantMessage,
	type AssistantMessageEvent,
	type Context,
	createModels,
	createProvider,
	type Message,
	type Model,
	type ProviderStreams,
	type SimpleStreamOptions,
} from "@earendil-works/pi-ai";
import { streamSimple } from "@earendil-works/pi-ai/compat";
import type { ServerConfig } from "./config.ts";
import { loadConfig } from "./config.ts";
import { PiServerError, PiServerErrorCode, type PiServerErrorResponse } from "./error-codes.ts";
import { encodeErrorEvent, encodeProxyEvent } from "./event-encoding.ts";
import { ReceiveUploadError, receiveUpload } from "./receive-upload.ts";
import { CHUNK_ENDPOINT, type RequestChunkBody, receiveRequestChunk } from "./request-chunks.ts";
import { deletePersistedSession, loadPersistedSessions, savePersistedSession } from "./session-persistence.ts";
import {
	appendCompactionEntry,
	appendMessages,
	appendSessionEntries,
	deleteSession as deleteSessionFromStore,
	getOrCreateSession,
	getSession,
	getSessionBranch,
	listSessions,
	replaceMessages,
	replaceSessionTree,
	type SessionState,
	type SessionStaticContext,
	setStaticContext,
	switchSessionLeaf,
} from "./session-store.ts";

export { loadConfig, type ServerConfig } from "./config.ts";

interface PackageMetadata {
	version: string;
}

const packageMetadata = createRequire(import.meta.url)("../package.json") as PackageMetadata;
const PI_SERVER_VERSION = packageMetadata.version;

interface SessionInitBody {
	sessionId: string;
	staticContext?: SessionStaticContext;
}

interface StreamRequestBody {
	sessionId: string;
	runId?: string;
	model: Model<any>;
	options?: SimpleStreamOptions;
	staticContext?: SessionStaticContext;
	ephemeralMessages?: Message[];
	contextOverlay?: Message[];
}

interface SessionSyncBody {
	sessionId: string;
	messages: Message[];
	staticContext?: SessionStaticContext;
}

interface SessionAppendBody {
	sessionId: string;
	messages: Message[];
	staticContext?: SessionStaticContext;
}

interface SessionTreeSyncBody {
	sessionId: string;
	entries: SessionTreeEntry[];
	leafId: string | null;
	staticContext?: SessionStaticContext;
}

interface SessionTreeSwitchBody {
	sessionId: string;
	leafId: string | null;
}

interface SessionCompactBody {
	sessionId: string;
	runId?: string;
	model: Model<any>;
	options?: SimpleStreamOptions;
	settings?: CompactionSettings;
	preparation?: CompactionPreparationOptions;
	customInstructions?: string;
	baseTreeHash?: string;
	fullResponse?: boolean;
	streamResponse?: boolean;
}

function createRequestModels(model: Model<any>, options: SimpleStreamOptions) {
	const models = createModels();
	const requestStream: ProviderStreams["streamSimple"] = (requestModel, context, streamOptions) =>
		streamSimple(requestModel, context, {
			...streamOptions,
			...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
		});
	models.setProvider(
		createProvider({
			id: model.provider,
			name: model.provider,
			models: [model],
			auth: {
				apiKey: {
					name: "pi-server request auth",
					resolve: async () => ({ auth: { apiKey: options.apiKey, headers: options.headers } }),
				},
			},
			api: {
				stream: requestStream,
				streamSimple: requestStream,
			},
		}),
	);
	return models;
}

interface StreamRunRecord {
	sessionId: string;
	runId: string;
	kind: "stream" | "compact";
	status: "running" | "completed" | "failed" | "aborted";
	message?: AssistantMessage;
	compactResult?: SessionCompactSuccessBody;
	errorMessage?: string;
	createdAt: number;
	updatedAt: number;
	cancelRequested: boolean;
	controller: AbortController;
	settled: Promise<void>;
	resolveSettled: (() => void) | undefined;
}

const STREAM_HEARTBEAT = ": keep-alive\n\n";
const JSON_HEARTBEAT = " \n";
const STREAM_HEARTBEAT_INTERVAL_MS = 25_000;
const STREAM_RUN_TTL_MS = 60 * 60 * 1000; // 1 hour
const streamRuns = new Map<string, StreamRunRecord>();

function readBody(req: IncomingMessage): Promise<string> {
	return new Promise((resolve, reject) => {
		const chunks: Buffer[] = [];
		req.on("data", (chunk: Buffer) => chunks.push(chunk));
		req.on("end", () => resolve(Buffer.concat(chunks).toString("utf-8")));
		req.on("error", reject);
	});
}

function sendJson(res: ServerResponse, status: number, body: unknown): void {
	const data = JSON.stringify(body);
	res.writeHead(status, { "Content-Type": "application/json", "Content-Length": Buffer.byteLength(data) });
	res.end(data);
}

function sendError(
	res: ServerResponse,
	status: number,
	message: string,
	code?: PiServerErrorCode,
	details?: Record<string, unknown>,
): void {
	const body: PiServerErrorResponse = { error: message };
	if (code) body.code = code;
	if (details) body.details = details;
	sendJson(res, status, body);
}

function logRequestError(req: IncomingMessage, error: unknown): void {
	const message = error instanceof Error ? error.stack || error.message : String(error);
	console.error(`${req.method ?? "UNKNOWN"} ${req.url ?? "/"} failed: ${message}`);
}

function authenticate(config: ServerConfig, req: IncomingMessage): boolean {
	if (!config.authToken) return true;
	const header = req.headers.authorization;
	if (!header) return false;
	const token = header.startsWith("Bearer ") ? header.slice(7) : header;
	return token === config.authToken;
}

function sessionResponseBody(session: SessionState) {
	return {
		sessionId: session.sessionId,
		staticContextHash: session.staticContextHash,
		treeHash: session.treeHash,
		messageCount: session.messages.length,
		entryCount: session.entries.length,
		leafId: session.leafId,
		revision: session.revision,
	};
}

function persistSession(config: ServerConfig, session: SessionState): void {
	savePersistedSession(config.sessionStoreDir, session);
}

function runKey(sessionId: string, runId: string): string {
	return `${sessionId}\0${runId}`;
}

function getStreamRun(sessionId: string, runId: string): StreamRunRecord | undefined {
	return streamRuns.get(runKey(sessionId, runId));
}

function createRunRecord(sessionId: string, runId: string, kind: StreamRunRecord["kind"]): StreamRunRecord {
	let resolveSettled: (() => void) | undefined;
	const settled = new Promise<void>((resolve) => {
		resolveSettled = resolve;
	});
	return {
		sessionId,
		runId,
		kind,
		status: "running",
		createdAt: Date.now(),
		updatedAt: Date.now(),
		cancelRequested: false,
		controller: new AbortController(),
		settled,
		resolveSettled,
	};
}

function settleRun(run: StreamRunRecord | undefined): void {
	if (!run) return;
	const resolve = run.resolveSettled;
	run.resolveSettled = undefined;
	resolve?.();
}

function startStreamRun(sessionId: string, runId: string, kind: StreamRunRecord["kind"] = "stream"): StreamRunRecord {
	const existing = getStreamRun(sessionId, runId);
	if (existing?.status === "completed" || existing?.status === "aborted") return existing;
	const run = existing ?? createRunRecord(sessionId, runId, kind);
	if (existing) {
		run.message = undefined;
		run.errorMessage = undefined;
		run.cancelRequested = false;
		run.controller = new AbortController();
		let resolveSettled: (() => void) | undefined;
		run.settled = new Promise<void>((resolve) => {
			resolveSettled = resolve;
		});
		run.resolveSettled = resolveSettled;
	}
	run.kind = kind;
	run.status = "running";
	run.updatedAt = Date.now();
	streamRuns.set(runKey(sessionId, runId), run);
	return run;
}

function streamRunResponseBody(run: StreamRunRecord) {
	return {
		sessionId: run.sessionId,
		runId: run.runId,
		status: run.status,
		createdAt: run.createdAt,
		updatedAt: run.updatedAt,
		...(run.message ? { message: run.message } : {}),
		...(run.compactResult ? { compactResult: run.compactResult } : {}),
		...(run.errorMessage ? { errorMessage: run.errorMessage } : {}),
	};
}

function replayStreamRun(run: StreamRunRecord): ProxyAssistantMessageEvent[] {
	if (!run.message) return [];
	if (
		run.message.stopReason !== "stop" &&
		run.message.stopReason !== "length" &&
		run.message.stopReason !== "toolUse" &&
		run.message.stopReason !== "deferred"
	) {
		return [];
	}
	const events: ProxyAssistantMessageEvent[] = [{ type: "start" }];
	for (const [contentIndex, content] of run.message.content.entries()) {
		switch (content.type) {
			case "text":
				events.push(
					{ type: "text_start", contentIndex },
					{ type: "text_delta", contentIndex, delta: content.text },
					{ type: "text_end", contentIndex, contentSignature: content.textSignature },
				);
				break;
			case "thinking":
				events.push(
					{ type: "thinking_start", contentIndex },
					{ type: "thinking_delta", contentIndex, delta: content.thinking },
					{ type: "thinking_end", contentIndex, contentSignature: content.thinkingSignature },
				);
				break;
			case "toolCall":
				events.push(
					{ type: "toolcall_start", contentIndex, id: content.id, toolName: content.name },
					{ type: "toolcall_delta", contentIndex, delta: JSON.stringify(content.arguments) },
					{ type: "toolcall_end", contentIndex, toolCall: content },
				);
				break;
			default:
				break;
		}
	}
	events.push({
		type: "done",
		reason: run.message.stopReason,
		usage: run.message.usage,
		deferred: run.message.deferred,
		message: run.message,
	});
	return events;
}

function completeStreamRun(run: StreamRunRecord | undefined, message: AssistantMessage): void {
	if (!run) return;
	if (run.cancelRequested) {
		run.status = "aborted";
		run.message = undefined;
		run.errorMessage = "Stream run aborted";
		run.updatedAt = Date.now();
		return;
	}
	run.status = "completed";
	run.message = message;
	run.errorMessage = undefined;
	run.updatedAt = Date.now();
}

function failStreamRun(run: StreamRunRecord | undefined, errorMessage: string): void {
	if (!run) return;
	if (run.cancelRequested) {
		run.status = "aborted";
		run.errorMessage = "Stream run aborted";
		run.updatedAt = Date.now();
		return;
	}
	run.status = "failed";
	run.errorMessage = errorMessage;
	run.updatedAt = Date.now();
}

function touchStreamRun(run: StreamRunRecord | undefined): void {
	if (run) run.updatedAt = Date.now();
}

function writeStreamEvent(res: ServerResponse, event: ProxyAssistantMessageEvent): void {
	if (!res.writableEnded && !res.destroyed) {
		res.write(encodeProxyEvent(event));
	}
}

function writeStreamError(res: ServerResponse, message: string): void {
	if (!res.writableEnded && !res.destroyed) {
		res.write(encodeErrorEvent(message));
	}
}

function endStreamResponse(res: ServerResponse): void {
	if (!res.writableEnded && !res.destroyed) {
		res.end();
	}
}

function cleanupExpiredStreamRuns(nowMs: number): void {
	for (const [key, run] of streamRuns) {
		if (nowMs - run.updatedAt > STREAM_RUN_TTL_MS) {
			streamRuns.delete(key);
		}
	}
}

function deleteStreamRunsForSession(sessionId: string): void {
	for (const [key, run] of streamRuns) {
		if (run.sessionId === sessionId) streamRuns.delete(key);
	}
}

const streamRunCleanupTimer = setInterval(() => cleanupExpiredStreamRuns(Date.now()), STREAM_RUN_TTL_MS / 4);
streamRunCleanupTimer.unref();

function sessionHistoryFullResponseBody(session: SessionState, baseMessageCount: number) {
	return {
		sessionId: session.sessionId,
		staticContext: session.staticContext,
		staticContextHash: session.staticContextHash,
		treeHash: session.treeHash,
		messageCount: session.messages.length,
		entryCount: session.entries.length,
		leafId: session.leafId,
		revision: session.revision,
		entries: session.entries,
		baseMessageCount,
		messages: session.messages.slice(baseMessageCount),
	};
}

function sessionTreePatchResponseBody(
	session: SessionState,
	baseMessageCount: number,
	entriesFrom: number,
	baseRevision: number | undefined,
) {
	return {
		sessionId: session.sessionId,
		staticContext: session.staticContext,
		staticContextHash: session.staticContextHash,
		treeHash: session.treeHash,
		messageCount: session.messages.length,
		entryCount: session.entries.length,
		leafId: session.leafId,
		revision: session.revision,
		baseMessageCount,
		messages: session.messages.slice(baseMessageCount),
		treePatch: {
			entriesFrom,
			baseRevision,
			entries: session.entries.slice(entriesFrom),
			leafId: session.leafId,
			revision: session.revision,
		},
	};
}

type PreparedCompaction = Parameters<typeof compactLegacy>[0];

interface PreparedSessionCompact {
	session: SessionState;
	sessionId: string;
	revision: number;
	treeHash: string;
	leafId: string | null;
	entryCount: number;
	preparation: PreparedCompaction;
	options: SimpleStreamOptions;
}

interface SessionCompactSuccessBody {
	success: true;
	compaction: Omit<LegacyCompactResult, "retainedTail">;
	compactionEntry: SessionTreeEntry;
	sessionId: string;
	staticContextHash: string;
	treeHash: string;
	messageCount: number;
	entryCount: number;
	leafId: string | null;
	revision: number;
	staticContext?: SessionStaticContext;
	treePatch?: {
		baseTreeHash: string;
		entriesFrom: number;
		entries: SessionTreeEntry[];
		leafId: string | null;
		revision: number;
	};
	entries?: SessionTreeEntry[];
	messages?: Message[];
}

interface SessionCompactHttpResponse {
	status: number;
	body: PiServerErrorResponse | SessionCompactSuccessBody;
}

function handleSessionInit(config: ServerConfig, body: SessionInitBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (body.staticContext) {
		setStaticContext(body.sessionId, body.staticContext);
	} else {
		getOrCreateSession(body.sessionId);
	}
	const session = getSession(body.sessionId)!;
	persistSession(config, session);
	sendJson(res, 200, sessionResponseBody(session));
}

function handleSessionUpdate(
	config: ServerConfig,
	body: SessionInitBody & { staticContext: SessionStaticContext },
	res: ServerResponse,
): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!body.staticContext) {
		sendError(res, 400, "staticContext is required for update", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	setStaticContext(body.sessionId, body.staticContext);
	const session = getSession(body.sessionId)!;
	persistSession(config, session);
	sendJson(res, 200, sessionResponseBody(session));
}

function handleSessionSync(config: ServerConfig, body: SessionSyncBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!Array.isArray(body.messages)) {
		sendError(res, 400, "messages is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (body.staticContext) {
		setStaticContext(body.sessionId, body.staticContext);
	}
	const session = replaceMessages(body.sessionId, body.messages);
	persistSession(config, session);
	sendJson(res, 200, sessionResponseBody(session));
}

function handleSessionAppend(config: ServerConfig, body: SessionAppendBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!Array.isArray(body.messages)) {
		sendError(res, 400, "messages is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (body.staticContext) {
		setStaticContext(body.sessionId, body.staticContext);
	}
	const session = appendMessages(body.sessionId, body.messages);
	persistSession(config, session);
	sendJson(res, 200, sessionResponseBody(session));
}

function handleSessionTreeSync(config: ServerConfig, body: SessionTreeSyncBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!Array.isArray(body.entries)) {
		sendError(res, 400, "entries is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (body.staticContext) {
		setStaticContext(body.sessionId, body.staticContext);
	}
	const session = replaceSessionTree(body.sessionId, body.entries, body.leafId ?? null);
	persistSession(config, session);
	sendJson(res, 200, sessionResponseBody(session));
}

function handleSessionTreeAppend(config: ServerConfig, body: SessionTreeSyncBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!Array.isArray(body.entries)) {
		sendError(res, 400, "entries is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	try {
		if (body.staticContext) {
			setStaticContext(body.sessionId, body.staticContext);
		}
		const session = appendSessionEntries(body.sessionId, body.entries, body.leafId ?? null);
		persistSession(config, session);
		sendJson(res, 200, sessionResponseBody(session));
	} catch (error) {
		if (error instanceof PiServerError) {
			sendError(res, 400, error.message, error.code, error.details);
		} else {
			const message = error instanceof Error ? error.message : String(error);
			sendError(res, 500, message, PiServerErrorCode.INTERNAL_ERROR);
		}
	}
}

function handleSessionTreeSwitch(config: ServerConfig, body: SessionTreeSwitchBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	try {
		const session = switchSessionLeaf(body.sessionId, body.leafId ?? null);
		persistSession(config, session);
		sendJson(res, 200, sessionResponseBody(session));
	} catch (error) {
		if (error instanceof PiServerError) {
			sendError(res, 400, error.message, error.code, error.details);
		} else {
			const message = error instanceof Error ? error.message : String(error);
			sendError(res, 500, message, PiServerErrorCode.INTERNAL_ERROR);
		}
	}
}

function prepareSessionCompact(body: SessionCompactBody): PreparedSessionCompact | SessionCompactHttpResponse {
	if (!body.sessionId) {
		return { status: 400, body: { error: "sessionId is required" } };
	}
	if (!body.model) {
		return { status: 400, body: { error: "model is required" } };
	}

	const session = getSession(body.sessionId);
	if (!session) {
		return { status: 404, body: { error: "session not found", code: PiServerErrorCode.SESSION_NOT_FOUND } };
	}

	const entries = getSessionBranch(session);
	const leaf = entries.at(-1);
	if (leaf?.type === "compaction") {
		if (!leaf.firstKeptEntryId) {
			return { status: 400, body: { error: "Stored compaction is missing firstKeptEntryId" } };
		}
		return {
			status: 200,
			body: {
				success: true,
				compaction: {
					summary: leaf.summary,
					firstKeptEntryId: leaf.firstKeptEntryId,
					tokensBefore: leaf.tokensBefore,
					details: leaf.details,
				},
				compactionEntry: leaf,
				...sessionResponseBody(session),
				entries: session.entries,
				messages: session.messages,
			},
		};
	}
	const preparationResult = prepareLegacyCompaction(
		entries,
		body.settings ?? DEFAULT_COMPACTION_SETTINGS,
		body.preparation,
	);
	if (!preparationResult.ok) {
		return { status: 400, body: { error: preparationResult.error.message } };
	}
	if (!preparationResult.value) {
		return { status: 400, body: { error: "Nothing to compact" } };
	}

	const options = body.options ?? {};
	return {
		session,
		sessionId: session.sessionId,
		revision: session.revision,
		treeHash: session.treeHash,
		leafId: session.leafId,
		entryCount: session.entries.length,
		preparation: preparationResult.value,
		options,
	};
}

function sessionCompactConflict(
	body: SessionCompactBody,
	prepared: PreparedSessionCompact,
	currentSession: SessionState | undefined,
): SessionCompactHttpResponse {
	return {
		status: 409,
		body: {
			error: "Session changed while compaction was running",
			code: PiServerErrorCode.SESSION_STATE_CONFLICT,
			details: {
				sessionId: body.sessionId,
				expectedRevision: prepared.revision,
				expectedTreeHash: prepared.treeHash,
				expectedLeafId: prepared.leafId,
				actualRevision: currentSession?.revision ?? null,
				actualTreeHash: currentSession?.treeHash ?? null,
				actualLeafId: currentSession?.leafId ?? null,
			},
		},
	};
}

async function completeSessionCompact(
	config: ServerConfig,
	body: SessionCompactBody,
	prepared: PreparedSessionCompact,
	run?: StreamRunRecord,
): Promise<SessionCompactHttpResponse> {
	const result = await compactLegacy(
		prepared.preparation,
		createRequestModels(body.model, prepared.options),
		body.model,
		body.customInstructions,
		run?.controller.signal,
		prepared.options.reasoning,
	);
	if (!result.ok) {
		return { status: 500, body: { error: result.error.message, code: PiServerErrorCode.INTERNAL_ERROR } };
	}
	if (run?.cancelRequested || run?.controller.signal.aborted) {
		return { status: 409, body: { error: "Compaction aborted", code: PiServerErrorCode.INVALID_REQUEST } };
	}

	const currentSession = getSession(prepared.sessionId);
	if (
		!currentSession ||
		currentSession !== prepared.session ||
		currentSession.revision !== prepared.revision ||
		currentSession.treeHash !== prepared.treeHash ||
		currentSession.leafId !== prepared.leafId
	) {
		return sessionCompactConflict(body, prepared, currentSession);
	}

	const baseTreeHash = prepared.treeHash;
	const baseEntryCount = prepared.entryCount;
	const compaction = result.value;
	const { session: updatedSession, entry: compactionEntry } = appendCompactionEntry(body.sessionId, compaction);
	persistSession(config, updatedSession);
	if (!body.fullResponse && body.baseTreeHash === baseTreeHash) {
		return {
			status: 200,
			body: {
				success: true,
				compaction: compaction satisfies LegacyCompactResult,
				compactionEntry,
				...sessionResponseBody(updatedSession),
				staticContext: updatedSession.staticContext,
				treePatch: {
					baseTreeHash,
					entriesFrom: baseEntryCount,
					entries: [compactionEntry],
					leafId: updatedSession.leafId,
					revision: updatedSession.revision,
				},
			},
		};
	}
	return {
		status: 200,
		body: {
			success: true,
			compaction: compaction satisfies LegacyCompactResult,
			compactionEntry,
			...sessionResponseBody(updatedSession),
			staticContext: updatedSession.staticContext,
			entries: updatedSession.entries,
			messages: updatedSession.messages,
		},
	};
}

function writeServerSentEvent(res: ServerResponse, event: string, body: unknown): void {
	res.write(`event: ${event}\n`);
	res.write(`data: ${JSON.stringify(body)}\n\n`);
}

async function handleSessionCompactStream(
	config: ServerConfig,
	body: SessionCompactBody,
	prepared: PreparedSessionCompact,
	res: ServerResponse,
	run?: StreamRunRecord,
): Promise<void> {
	res.writeHead(200, {
		"Content-Type": "text/event-stream",
		"Cache-Control": "no-cache",
		Connection: "keep-alive",
	});
	res.flushHeaders();
	res.write(STREAM_HEARTBEAT);

	const heartbeat = setInterval(() => {
		touchStreamRun(run);
		if (!res.writableEnded) {
			res.write(STREAM_HEARTBEAT);
		}
	}, STREAM_HEARTBEAT_INTERVAL_MS);
	heartbeat.unref();

	try {
		const result = await completeSessionCompact(config, body, prepared, run);
		finishCompactRun(run, result);
		writeServerSentEvent(res, result.status >= 400 ? "error" : "result", result.body);
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		if (run?.status === "running") {
			run.status = run.cancelRequested ? "aborted" : "failed";
			run.errorMessage = message;
			run.updatedAt = Date.now();
		}
		writeServerSentEvent(res, "error", { error: message });
	} finally {
		clearInterval(heartbeat);
		settleRun(run);
		res.end();
	}
}

async function handleSessionCompactJsonStream(
	config: ServerConfig,
	body: SessionCompactBody,
	prepared: PreparedSessionCompact,
	res: ServerResponse,
	run?: StreamRunRecord,
): Promise<void> {
	res.writeHead(200, {
		"Content-Type": "application/json",
		"Cache-Control": "no-cache",
		Connection: "keep-alive",
	});
	res.flushHeaders();
	res.write(JSON_HEARTBEAT);

	const heartbeat = setInterval(() => {
		touchStreamRun(run);
		if (!res.writableEnded) {
			res.write(JSON_HEARTBEAT);
		}
	}, STREAM_HEARTBEAT_INTERVAL_MS);
	heartbeat.unref();

	try {
		const result = await completeSessionCompact(config, body, prepared, run);
		finishCompactRun(run, result);
		res.write(JSON.stringify(result.body));
	} catch (err) {
		const message = err instanceof Error ? err.message : String(err);
		if (run?.status === "running") {
			run.status = run.cancelRequested ? "aborted" : "failed";
			run.errorMessage = message;
			run.updatedAt = Date.now();
		}
		res.write(JSON.stringify({ error: message }));
	} finally {
		clearInterval(heartbeat);
		settleRun(run);
		res.end();
	}
}

function finishCompactRun(run: StreamRunRecord | undefined, result: SessionCompactHttpResponse): void {
	if (!run) return;
	if (run.cancelRequested || run.controller.signal.aborted) {
		run.status = "aborted";
		run.errorMessage = "Compaction aborted";
	} else {
		run.status = result.status >= 400 ? "failed" : "completed";
		if (result.status >= 400 && "error" in result.body) {
			run.errorMessage = result.body.error;
		} else if ("success" in result.body) {
			run.compactResult = { ...result.body, entries: result.body.entries?.slice() };
		}
	}
	run.updatedAt = Date.now();
}

async function handleSessionCompact(
	config: ServerConfig,
	body: SessionCompactBody,
	res: ServerResponse,
): Promise<void> {
	const existingRun = body.runId ? getStreamRun(body.sessionId, body.runId) : undefined;
	if (existingRun?.status === "aborted") {
		sendJson(res, 409, { error: "Compaction run was aborted", code: PiServerErrorCode.INVALID_REQUEST });
		return;
	}
	if (existingRun?.status === "completed" && existingRun.compactResult) {
		sendJson(res, 200, existingRun.compactResult);
		return;
	}
	const prepared = prepareSessionCompact(body);
	if ("status" in prepared) {
		sendJson(res, prepared.status, prepared.body);
		return;
	}

	let run: StreamRunRecord | undefined;
	if (body.runId) {
		const existingRun = getStreamRun(body.sessionId, body.runId);
		if (existingRun?.status === "aborted") {
			sendJson(res, 409, { error: "Compaction run was aborted", code: PiServerErrorCode.INVALID_REQUEST });
			return;
		}
		if (existingRun?.status === "running") {
			sendError(res, 409, "A run with this runId is already in progress", PiServerErrorCode.RUN_IN_PROGRESS);
			return;
		}
		run = startStreamRun(body.sessionId, body.runId, "compact");
	}

	if (body.streamResponse) {
		await handleSessionCompactStream(config, body, prepared, res, run);
		return;
	}

	await handleSessionCompactJsonStream(config, body, prepared, res, run);
}

function handleSessionHistory(
	sessionId: string,
	from: number | undefined,
	entriesFrom: number | undefined,
	revision: number | undefined,
	baseTreeHash: string | undefined,
	res: ServerResponse,
): void {
	const session = getSession(sessionId);
	if (!session) {
		sendError(res, 404, "session not found", PiServerErrorCode.SESSION_NOT_FOUND);
		return;
	}
	const baseMessageCount = from ?? 0;
	if (
		entriesFrom !== undefined &&
		entriesFrom <= session.entries.length &&
		(revision === undefined || revision <= session.revision) &&
		(baseTreeHash === undefined || baseTreeHash === session.prefixHashes[entriesFrom])
	) {
		sendJson(res, 200, sessionTreePatchResponseBody(session, baseMessageCount, entriesFrom, revision));
		return;
	}
	sendJson(res, 200, sessionHistoryFullResponseBody(session, baseMessageCount));
}

function handleSessionRun(sessionId: string, runId: string, res: ServerResponse): void {
	const run = getStreamRun(sessionId, runId);
	if (!run) {
		sendError(res, 404, "run not found", PiServerErrorCode.RUN_NOT_FOUND);
		return;
	}
	sendJson(res, 200, streamRunResponseBody(run));
}

async function handleSessionRunAbort(sessionId: string, runId: string, res: ServerResponse): Promise<void> {
	if (!sessionId || !runId) {
		sendError(res, 400, "sessionId and runId are required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}

	let run = getStreamRun(sessionId, runId);
	if (!run) {
		run = createRunRecord(sessionId, runId, "stream");
		run.status = "aborted";
		run.cancelRequested = true;
		run.errorMessage = "Stream run aborted before it started";
		run.updatedAt = Date.now();
		settleRun(run);
		streamRuns.set(runKey(sessionId, runId), run);
		sendJson(res, 200, streamRunResponseBody(run));
		return;
	}

	if (run.status !== "running") {
		await run.settled;
		sendJson(res, 200, streamRunResponseBody(run));
		return;
	}

	run.cancelRequested = true;
	run.updatedAt = Date.now();
	run.controller.abort();
	await run.settled;
	sendJson(res, 200, streamRunResponseBody(run));
}

export function buildStreamContext(
	session: SessionState,
	body: Pick<StreamRequestBody, "contextOverlay" | "ephemeralMessages">,
): Context {
	const messages = body.contextOverlay ?? [...session.messages, ...(body.ephemeralMessages ?? [])];
	return {
		systemPrompt: session.staticContext?.systemPrompt,
		messages,
		tools: session.staticContext?.tools,
	};
}

function handleStream(config: ServerConfig, body: StreamRequestBody, res: ServerResponse): void {
	if (!body.sessionId) {
		sendError(res, 400, "sessionId is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}
	if (!body.model) {
		sendError(res, 400, "model is required", PiServerErrorCode.REQUIRED_FIELD_MISSING);
		return;
	}

	cleanupExpiredStreamRuns(Date.now());

	const session = getOrCreateSession(body.sessionId);

	if (body.staticContext) {
		setStaticContext(body.sessionId, body.staticContext);
		persistSession(config, session);
	}

	if (!session.staticContext && !body.staticContext) {
		sendError(
			res,
			400,
			"Session has no static context. Initialize with /api/session/init first.",
			PiServerErrorCode.SESSION_NO_STATIC_CONTEXT,
		);
		return;
	}

	if (body.ephemeralMessages !== undefined && !Array.isArray(body.ephemeralMessages)) {
		sendError(res, 400, "ephemeralMessages must be an array", PiServerErrorCode.INVALID_REQUEST);
		return;
	}
	if (body.contextOverlay !== undefined && !Array.isArray(body.contextOverlay)) {
		sendError(res, 400, "contextOverlay must be an array", PiServerErrorCode.INVALID_REQUEST);
		return;
	}

	const context = buildStreamContext(session, body);
	const existingRun = body.runId ? getStreamRun(body.sessionId, body.runId) : undefined;

	const resolvedModel = body.model;

	if (existingRun?.status === "completed") {
		res.writeHead(200, {
			"Content-Type": "text/event-stream",
			"Cache-Control": "no-cache",
			Connection: "keep-alive",
		});
		res.flushHeaders();
		res.write(STREAM_HEARTBEAT);
		for (const event of replayStreamRun(existingRun)) {
			writeStreamEvent(res, event);
		}
		endStreamResponse(res);
		return;
	}
	if (existingRun?.status === "aborted") {
		sendError(res, 409, "The stream run was aborted", PiServerErrorCode.INVALID_REQUEST);
		return;
	}
	if (existingRun?.status === "running") {
		sendError(res, 409, "A stream with this runId is already in progress", PiServerErrorCode.RUN_IN_PROGRESS);
		return;
	}

	const run = body.runId ? startStreamRun(body.sessionId, body.runId) : undefined;
	const streamOptions: SimpleStreamOptions = {
		...(body.options ?? {}),
		...(run ? { signal: run.controller.signal } : {}),
	};

	res.writeHead(200, {
		"Content-Type": "text/event-stream",
		"Cache-Control": "no-cache",
		Connection: "keep-alive",
	});
	res.flushHeaders();
	res.write(STREAM_HEARTBEAT);

	const heartbeat = setInterval(() => {
		touchStreamRun(run);
		if (!res.writableEnded && !res.destroyed) {
			res.write(STREAM_HEARTBEAT);
		}
	}, STREAM_HEARTBEAT_INTERVAL_MS);
	heartbeat.unref();

	let stream: AsyncIterable<AssistantMessageEvent>;
	try {
		stream = streamSimple(resolvedModel, context, streamOptions);
	} catch (err) {
		clearInterval(heartbeat);
		const message = err instanceof Error ? err.message : String(err);
		failStreamRun(run, message);
		settleRun(run);
		writeStreamError(res, message);
		endStreamResponse(res);
		return;
	}

	void (async () => {
		try {
			for await (const event of stream) {
				const proxyEvent = toProxyEvent(event);
				if (proxyEvent) {
					touchStreamRun(run);
					writeStreamEvent(res, proxyEvent);
				}
				if (event.type === "done") {
					completeStreamRun(run, event.message);
				} else if (event.type === "error") {
					failStreamRun(run, event.error.errorMessage ?? event.reason);
				}
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			failStreamRun(run, message);
			writeStreamError(res, message);
		} finally {
			clearInterval(heartbeat);
			if (run?.status === "running") {
				failStreamRun(run, run.cancelRequested ? "Stream run aborted" : "Stream ended before a terminal event");
			}
			settleRun(run);
			endStreamResponse(res);
		}
	})();
}

async function handlePostRequest(
	config: ServerConfig,
	pathname: string,
	body: unknown,
	res: ServerResponse,
): Promise<boolean> {
	if (pathname === "/api/receive") {
		try {
			sendJson(res, 200, receiveUpload(config.uploadDir, body));
		} catch (error) {
			if (!(error instanceof ReceiveUploadError)) throw error;
			sendJson(res, error.status, { error: error.message });
		}
		return true;
	}

	if (pathname === "/api/session/init") {
		handleSessionInit(config, body as SessionInitBody, res);
		return true;
	}

	if (pathname === "/api/session/update") {
		handleSessionUpdate(config, body as SessionInitBody & { staticContext: SessionStaticContext }, res);
		return true;
	}

	if (pathname === "/api/session/sync") {
		handleSessionSync(config, body as SessionSyncBody, res);
		return true;
	}

	if (pathname === "/api/session/append") {
		handleSessionAppend(config, body as SessionAppendBody, res);
		return true;
	}

	if (pathname === "/api/session/tree/sync") {
		handleSessionTreeSync(config, body as SessionTreeSyncBody, res);
		return true;
	}

	if (pathname === "/api/session/tree/append") {
		handleSessionTreeAppend(config, body as SessionTreeSyncBody, res);
		return true;
	}

	if (pathname === "/api/session/tree/switch") {
		handleSessionTreeSwitch(config, body as SessionTreeSwitchBody, res);
		return true;
	}

	if (pathname === "/api/session/compact") {
		await handleSessionCompact(config, body as SessionCompactBody, res);
		return true;
	}

	if (pathname === "/api/stream") {
		handleStream(config, body as StreamRequestBody, res);
		return true;
	}

	return false;
}

export function createPiServer(configOverride?: Partial<ServerConfig>): HttpServer {
	const config = loadConfig(configOverride);
	loadPersistedSessions(config.sessionStoreDir);

	const server = createServer(async (req, res) => {
		const url = new URL(req.url ?? "/", "http://localhost");

		if (req.method === "GET" && url.pathname === "/health") {
			sendJson(res, 200, { status: "ok" });
			return;
		}

		if (!authenticate(config, req)) {
			const body =
				req.method === "GET" && url.pathname === "/"
					? { error: "Unauthorized", version: PI_SERVER_VERSION }
					: { error: "Unauthorized" };
			sendJson(res, 401, body);
			return;
		}

		if (req.method === "GET" && url.pathname === "/api/sessions") {
			sendJson(res, 200, { sessions: listSessions() });
			return;
		}

		const runMatch = /^\/api\/session\/([^/]+)\/runs\/([^/]+)$/.exec(url.pathname);
		if (req.method === "GET" && runMatch) {
			handleSessionRun(decodeURIComponent(runMatch[1]), decodeURIComponent(runMatch[2]), res);
			return;
		}

		const runAbortMatch = /^\/api\/session\/([^/]+)\/runs\/([^/]+)\/abort$/.exec(url.pathname);
		if (req.method === "POST" && runAbortMatch) {
			await readBody(req);
			await handleSessionRunAbort(decodeURIComponent(runAbortMatch[1]), decodeURIComponent(runAbortMatch[2]), res);
			return;
		}

		if (req.method === "GET" && url.pathname.startsWith("/api/session/") && url.pathname.endsWith("/history")) {
			const encodedSessionId = url.pathname.slice("/api/session/".length, -"/history".length);
			const fromParam = url.searchParams.get("from");
			const from = fromParam === null ? undefined : Number(fromParam);
			if (from !== undefined && (!Number.isInteger(from) || from < 0)) {
				sendError(res, 400, "from must be a non-negative integer", PiServerErrorCode.INVALID_REQUEST);
				return;
			}
			const entriesFromParam = url.searchParams.get("entriesFrom");
			const entriesFrom = entriesFromParam === null ? undefined : Number(entriesFromParam);
			if (entriesFrom !== undefined && (!Number.isInteger(entriesFrom) || entriesFrom < 0)) {
				sendError(res, 400, "entriesFrom must be a non-negative integer", PiServerErrorCode.INVALID_REQUEST);
				return;
			}
			const revisionParam = url.searchParams.get("revision");
			const revision = revisionParam === null ? undefined : Number(revisionParam);
			if (revision !== undefined && (!Number.isInteger(revision) || revision < 0)) {
				sendError(res, 400, "revision must be a non-negative integer", PiServerErrorCode.INVALID_REQUEST);
				return;
			}
			handleSessionHistory(
				decodeURIComponent(encodedSessionId),
				from,
				entriesFrom,
				revision,
				url.searchParams.get("baseTreeHash") ?? undefined,
				res,
			);
			return;
		}

		if (req.method === "POST" && url.pathname === CHUNK_ENDPOINT) {
			try {
				const body = JSON.parse(await readBody(req)) as RequestChunkBody;
				const chunkResult = receiveRequestChunk(body);
				if (!chunkResult.complete) {
					sendJson(res, 200, chunkResult.ack);
					return;
				}
				const handled = await handlePostRequest(
					config,
					chunkResult.target,
					JSON.parse(chunkResult.bodyJson) as unknown,
					res,
				);
				if (!handled && !res.headersSent) {
					sendError(res, 404, "Not found", PiServerErrorCode.INVALID_REQUEST);
				}
			} catch (err) {
				logRequestError(req, err);
				if (!res.headersSent) {
					sendError(res, 400, err instanceof Error ? err.message : String(err), PiServerErrorCode.INVALID_REQUEST);
				} else {
					res.write(encodeErrorEvent(err instanceof Error ? err.message : String(err)));
					res.end();
				}
			}
			return;
		}

		if (req.method === "POST") {
			try {
				const body = JSON.parse(await readBody(req)) as unknown;
				if (await handlePostRequest(config, url.pathname, body, res)) return;
				if (!res.headersSent) {
					sendError(res, 404, "Not found", PiServerErrorCode.INVALID_REQUEST);
					return;
				}
			} catch (err) {
				logRequestError(req, err);
				if (!res.headersSent) {
					sendError(res, 500, err instanceof Error ? err.message : String(err), PiServerErrorCode.INTERNAL_ERROR);
				} else {
					res.write(encodeErrorEvent(err instanceof Error ? err.message : String(err)));
					res.end();
				}
				return;
			}
		}

		if (req.method === "DELETE" && url.pathname.startsWith("/api/session/")) {
			const sessionId = decodeURIComponent(url.pathname.slice("/api/session/".length));
			deleteSessionFromStore(sessionId);
			deleteStreamRunsForSession(sessionId);
			deletePersistedSession(config.sessionStoreDir, sessionId);
			sendJson(res, 200, { deleted: sessionId });
			return;
		}

		sendError(res, 404, "Not found", PiServerErrorCode.INVALID_REQUEST);
	});

	return server;
}

function toProxyEvent(event: AssistantMessageEvent): ProxyAssistantMessageEvent | undefined {
	switch (event.type) {
		case "start":
			return { type: "start" };
		case "text_start":
			return { type: "text_start", contentIndex: event.contentIndex };
		case "text_delta":
			return { type: "text_delta", contentIndex: event.contentIndex, delta: event.delta };
		case "text_end":
			return {
				type: "text_end",
				contentIndex: event.contentIndex,
				contentSignature:
					event.partial.content[event.contentIndex]?.type === "text"
						? (event.partial.content[event.contentIndex] as { textSignature?: string }).textSignature
						: undefined,
			};
		case "thinking_start":
			return { type: "thinking_start", contentIndex: event.contentIndex };
		case "thinking_delta":
			return { type: "thinking_delta", contentIndex: event.contentIndex, delta: event.delta };
		case "thinking_end":
			return {
				type: "thinking_end",
				contentIndex: event.contentIndex,
				contentSignature:
					event.partial.content[event.contentIndex]?.type === "thinking"
						? (event.partial.content[event.contentIndex] as { thinkingSignature?: string }).thinkingSignature
						: undefined,
			};
		case "toolcall_start":
			return {
				type: "toolcall_start",
				contentIndex: event.contentIndex,
				id:
					event.partial.content[event.contentIndex]?.type === "toolCall"
						? (event.partial.content[event.contentIndex] as { id: string }).id
						: "",
				toolName:
					event.partial.content[event.contentIndex]?.type === "toolCall"
						? (event.partial.content[event.contentIndex] as { name: string }).name
						: "",
			};
		case "toolcall_delta":
			return { type: "toolcall_delta", contentIndex: event.contentIndex, delta: event.delta };
		case "toolcall_end":
			return { type: "toolcall_end", contentIndex: event.contentIndex, toolCall: event.toolCall };
		case "done":
			return {
				type: "done",
				reason: event.reason,
				usage: event.message.usage,
				deferred: event.message.deferred,
				message: event.message,
			};
		case "error":
			return {
				type: "error",
				reason: event.reason,
				errorMessage: event.error.errorMessage,
				usage: event.error.usage,
			};
		default:
			return undefined;
	}
}

export function startServer(configOverride?: Partial<ServerConfig>): HttpServer {
	const config = loadConfig(configOverride);
	const server = createPiServer(configOverride);
	server.listen(config.port, config.host, () => {
		const address = server.address();
		const port = typeof address === "object" && address !== null ? address.port : config.port;
		const host = config.host.includes(":") && !config.host.startsWith("[") ? `[${config.host}]` : config.host;
		console.log(`pi-server listening on ${host}:${port}`);
	});
	return server;
}
